import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createCrmCustomerContact,
  createCrmCustomerActivity,
  fetchCrmCustomer,
  fetchCrmCustomerContacts,
  fetchCrmCustomerActivities,
  isCrmClosedActivityError,
  isCrmDisabledError,
  isCrmViewBlockedError,
  isCrmWriteBlockedError,
  updateCrmCustomerContact,
  updateCrmCustomerActivity,
  updateCrmCustomerProfile,
} from '../../api/crmCustomers';
import { fetchAssignableUsers } from '../../api/userManagement';
import {
  EMPTY_ACTIVITY_FORM,
  EMPTY_CONTACT_FORM,
  EMPTY_PROFILE_FORM,
  buildActivityActionPayload,
  buildActivityPayload,
  buildContactPayload,
  buildOverviewSummary,
  buildProfilePayload,
  buildTimelineFeedItems,
  formatAssignableUserLabel,
  isActivityOpen,
  mapActivityToForm,
  mapContactToForm,
  mapProfileToForm,
  mergeActivityIntoList,
  mergeContactIntoList,
  mergeDoneActivityIntoList,
  normalizeOptionalString,
  sortActivities,
  sortContacts,
  sortDoneActivities,
  toDateInputValue,
} from './customerDetailUtils';

function sortAssignableUsers(users) {
  return [...users].sort((left, right) =>
    formatAssignableUserLabel(left).localeCompare(formatAssignableUserLabel(right))
  );
}

export default function useCustomerDetailController({
  connected,
  customerNumber,
  navigate,
  setConnected,
  setError,
}) {
  const [customer, setCustomer] = useState(null);
  const [profileFormValues, setProfileFormValues] = useState(EMPTY_PROFILE_FORM);
  const [contacts, setContacts] = useState([]);
  const [doneActivities, setDoneActivities] = useState([]);
  const [activities, setActivities] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshingContacts, setIsRefreshingContacts] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isSavingContact, setIsSavingContact] = useState(false);
  const [isSavingActivity, setIsSavingActivity] = useState(false);
  const [isLoadingAssignableUsers, setIsLoadingAssignableUsers] = useState(false);

  const [loadError, setLoadError] = useState('');
  const [profileSaveError, setProfileSaveError] = useState('');
  const [profileSuccessMessage, setProfileSuccessMessage] = useState('');
  const [contactError, setContactError] = useState('');
  const [activityError, setActivityError] = useState('');
  const [assignableUsersError, setAssignableUsersError] = useState('');
  const [notFound, setNotFound] = useState(false);

  const [showArchivedContacts, setShowArchivedContacts] = useState(false);
  const [hasLoadedArchivedContacts, setHasLoadedArchivedContacts] = useState(false);
  const [contactFormMode, setContactFormMode] = useState(null);
  const [editingContactId, setEditingContactId] = useState(null);
  const [contactFormValues, setContactFormValues] = useState(EMPTY_CONTACT_FORM);
  const [activityFormMode, setActivityFormMode] = useState(null);
  const [editingActivityId, setEditingActivityId] = useState(null);
  const [activityFormValues, setActivityFormValues] = useState(EMPTY_ACTIVITY_FORM);
  const [assigneeSearch, setAssigneeSearch] = useState('');

  const resetContactForm = useCallback(() => {
    setContactFormMode(null);
    setEditingContactId(null);
    setContactFormValues(EMPTY_CONTACT_FORM);
    setContactError('');
  }, []);

  const resetActivityForm = useCallback(() => {
    setActivityFormMode(null);
    setEditingActivityId(null);
    setActivityFormValues(EMPTY_ACTIVITY_FORM);
    setAssigneeSearch('');
    setActivityError('');
    setAssignableUsersError('');
  }, []);

  const resetPageState = useCallback(() => {
    setCustomer(null);
    setContacts([]);
    setDoneActivities([]);
    setActivities([]);
    setAssignableUsers([]);
    setProfileFormValues(EMPTY_PROFILE_FORM);
    setShowArchivedContacts(false);
    setHasLoadedArchivedContacts(false);
    setLoadError('');
    setProfileSaveError('');
    setProfileSuccessMessage('');
    setContactError('');
    setActivityError('');
    setAssignableUsersError('');
    setNotFound(false);
    setIsLoading(false);
    setIsRefreshingContacts(false);
    setIsSavingProfile(false);
    setIsSavingContact(false);
    setIsSavingActivity(false);
    setIsLoadingAssignableUsers(false);
    resetContactForm();
    resetActivityForm();
  }, [resetActivityForm, resetContactForm]);

  const clearCustomerRecords = useCallback(() => {
    setCustomer(null);
    setContacts([]);
    setDoneActivities([]);
    setActivities([]);
  }, []);

  const handleDisabledError = useCallback(
    (error) => {
      if (!isCrmDisabledError(error)) {
        return false;
      }

      setError(error.message || 'CRM is disabled for this tenant');
      navigate('/', { replace: true });
      return true;
    },
    [navigate, setError]
  );

  const handleViewBlockedError = useCallback(
    (error) => {
      if (!isCrmViewBlockedError(error)) {
        return false;
      }

      setConnected(false);
      return true;
    },
    [setConnected]
  );

  const handleWriteBlockedError = useCallback(
    (error, fallbackMessage, { allowAny409 = false } = {}) => {
      if (!(isCrmWriteBlockedError(error) || (allowAny409 && error?.status === 409))) {
        return false;
      }

      setConnected(false);
      setError(error.message || fallbackMessage);
      return true;
    },
    [setConnected, setError]
  );

  const handleNotFoundError = useCallback(
    (error) => {
      if (error?.status !== 404) {
        return false;
      }

      setNotFound(true);
      clearCustomerRecords();
      return true;
    },
    [clearCustomerRecords]
  );

  const handleMutationError = useCallback(
    (
      error,
      {
        allowAny409 = false,
        allowClosedActivity = false,
        allowNotFound = false,
        allowViewBlocked = false,
        fallbackMessage,
        setInlineError,
        writeBlockedMessage,
      } = {}
    ) => {
      if (handleDisabledError(error)) {
        return true;
      }

      if (allowViewBlocked && handleViewBlockedError(error)) {
        return true;
      }

      if (
        writeBlockedMessage &&
        handleWriteBlockedError(error, writeBlockedMessage, { allowAny409 })
      ) {
        return true;
      }

      if (allowNotFound && handleNotFoundError(error)) {
        return true;
      }

      if (allowClosedActivity && isCrmClosedActivityError(error)) {
        setInlineError?.(error.message);
        return true;
      }

      if (setInlineError) {
        setInlineError(error.message || fallbackMessage);
      }

      return Boolean(setInlineError);
    },
    [
      handleDisabledError,
      handleNotFoundError,
      handleViewBlockedError,
      handleWriteBlockedError,
    ]
  );

  useEffect(() => {
    if (!connected) {
      resetPageState();
      return undefined;
    }

    let active = true;

    const loadCustomerData = async () => {
      setIsLoading(true);
      setLoadError('');
      setProfileSaveError('');
      setProfileSuccessMessage('');
      setContactError('');
      setActivityError('');
      setAssignableUsersError('');
      setNotFound(false);
      setShowArchivedContacts(false);
      setHasLoadedArchivedContacts(false);
      resetContactForm();
      resetActivityForm();

      try {
        const [detail, loadedContacts, loadedOpenActivities, loadedDoneActivities] = await Promise.all([
          fetchCrmCustomer(customerNumber),
          fetchCrmCustomerContacts(customerNumber),
          fetchCrmCustomerActivities(customerNumber, { state: 'OPEN' }),
          fetchCrmCustomerActivities(customerNumber, { state: 'DONE' }),
        ]);

        if (!active) {
          return;
        }

        setCustomer(detail?.customer ?? null);
        setProfileFormValues(mapProfileToForm(detail?.profile));
        setContacts(sortContacts(loadedContacts));
        setActivities(sortActivities(loadedOpenActivities));
        setDoneActivities(sortDoneActivities(loadedDoneActivities));
      } catch (error) {
        if (!active) {
          return;
        }

        console.error('Failed to load CRM customer detail:', error);

        if (
          handleMutationError(error, {
            allowNotFound: true,
            allowViewBlocked: true,
            fallbackMessage: 'Failed to load the customer.',
            setInlineError: setLoadError,
          })
        ) {
          return;
        }
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    };

    loadCustomerData();

    return () => {
      active = false;
    };
  }, [
    connected,
    customerNumber,
    handleMutationError,
    resetActivityForm,
    resetContactForm,
    resetPageState,
  ]);

  const pageTitle = useMemo(
    () => (customer?.name ? customer.name : `Customer ${customerNumber}`),
    [customer?.name, customerNumber]
  );

  const visibleContacts = useMemo(
    () => (showArchivedContacts ? contacts : contacts.filter((contact) => Boolean(contact?.active))),
    [contacts, showArchivedContacts]
  );

  const activeContacts = useMemo(
    () => contacts.filter((contact) => Boolean(contact?.active)),
    [contacts]
  );

  const selectedEditingActivity = useMemo(
    () =>
      activities.find((activity) => Number(activity?.id ?? 0) === Number(editingActivityId ?? 0)) ??
      null,
    [activities, editingActivityId]
  );

  const activityContactOptions = useMemo(() => {
    const optionsById = new Map();

    activeContacts.forEach((contact) => {
      if (contact?.id != null) {
        optionsById.set(Number(contact.id), contact);
      }
    });

    const linkedContact = selectedEditingActivity?.contact;
    if (linkedContact?.id != null && !optionsById.has(Number(linkedContact.id))) {
      optionsById.set(Number(linkedContact.id), linkedContact);
    }

    return Array.from(optionsById.values());
  }, [activeContacts, selectedEditingActivity]);

  const selectedAssignee = useMemo(() => {
    const selectedId = Number(activityFormValues.assignedUserId || 0);
    if (!selectedId) {
      return null;
    }

    const loadedUser = assignableUsers.find((user) => Number(user?.id ?? 0) === selectedId);
    if (loadedUser) {
      return loadedUser;
    }

    const editingAssignee = selectedEditingActivity?.assignedUser;
    return Number(editingAssignee?.id ?? 0) === selectedId ? editingAssignee : null;
  }, [activityFormValues.assignedUserId, assignableUsers, selectedEditingActivity]);

  const assigneeOptions = useMemo(() => {
    const usersById = new Map();

    assignableUsers.forEach((user) => {
      if (user?.id != null) {
        usersById.set(Number(user.id), user);
      }
    });

    if (selectedAssignee?.id != null && !usersById.has(Number(selectedAssignee.id))) {
      usersById.set(Number(selectedAssignee.id), selectedAssignee);
    }

    return sortAssignableUsers(Array.from(usersById.values()));
  }, [assignableUsers, selectedAssignee]);

  const timelineFeedItems = useMemo(
    () => buildTimelineFeedItems(doneActivities),
    [doneActivities]
  );

  const overview = useMemo(
    () => buildOverviewSummary({ activities, contacts, profileFormValues }),
    [activities, contacts, profileFormValues]
  );

  const activeContactCount = useMemo(
    () => contacts.filter((contact) => Boolean(contact?.active)).length,
    [contacts]
  );

  const archivedContactCount = contacts.length - activeContactCount;

  const handleProfileFieldChange = useCallback((fieldName) => (event) => {
    const nextValue = event.target.value;
    setProfileFormValues((currentValues) => ({
      ...currentValues,
      [fieldName]: nextValue,
    }));
    setProfileSaveError('');
    setProfileSuccessMessage('');
  }, []);

  const handleContactFieldChange = useCallback((fieldName) => (event) => {
    const nextValue = fieldName === 'isPrimary' ? event.target.checked : event.target.value;
    setContactFormValues((currentValues) => ({
      ...currentValues,
      [fieldName]: nextValue,
    }));
    setContactError('');
  }, []);

  const handleActivityFieldChange = useCallback((fieldName) => (event) => {
    const nextValue = event.target.value;
    setActivityFormValues((currentValues) => ({
      ...currentValues,
      [fieldName]: nextValue,
    }));
    setActivityError('');
  }, []);

  const handleStartCreateContact = useCallback(() => {
    setContactFormMode('create');
    setEditingContactId(null);
    setContactFormValues(EMPTY_CONTACT_FORM);
    setContactError('');
  }, []);

  const handleStartEditContact = useCallback((contact) => {
    setContactFormMode('edit');
    setEditingContactId(contact?.id ?? null);
    setContactFormValues(mapContactToForm(contact));
    setContactError('');
  }, []);

  const handleStartCreateActivity = useCallback(() => {
    setActivityFormMode('create');
    setEditingActivityId(null);
    setActivityFormValues(EMPTY_ACTIVITY_FORM);
    setAssigneeSearch('');
    setActivityError('');
    setAssignableUsersError('');
  }, []);

  const handleStartEditActivity = useCallback((activity) => {
    setActivityFormMode('edit');
    setEditingActivityId(activity?.id ?? null);
    setActivityFormValues(mapActivityToForm(activity));
    setAssigneeSearch('');
    setActivityError('');
    setAssignableUsersError('');
  }, []);

  const handleAssigneeInputChange = useCallback((_event, nextValue, reason) => {
    if (reason === 'input') {
      setAssigneeSearch(nextValue);
    }

    if (reason === 'clear') {
      setAssigneeSearch('');
    }
  }, []);

  const handleAssigneeChange = useCallback((_event, nextUser) => {
    setAssignableUsers((currentUsers) => {
      if (!nextUser?.id) {
        return currentUsers;
      }

      if (currentUsers.some((user) => Number(user?.id ?? 0) === Number(nextUser.id))) {
        return currentUsers;
      }

      return sortAssignableUsers([...currentUsers, nextUser]);
    });
    setActivityFormValues((currentValues) => ({
      ...currentValues,
      assignedUserId: nextUser?.id ? String(nextUser.id) : '',
    }));
    setActivityError('');
  }, []);

  useEffect(() => {
    if (!activityFormMode) {
      return undefined;
    }

    let active = true;

    const loadAssignableUsers = async () => {
      setIsLoadingAssignableUsers(true);
      setAssignableUsersError('');

      try {
        const loadedUsers = await fetchAssignableUsers({ search: assigneeSearch });
        if (!active) {
          return;
        }

        setAssignableUsers(sortAssignableUsers(loadedUsers));
      } catch (error) {
        if (!active) {
          return;
        }

        console.error('Failed to load CRM assignable users:', error);

        if (
          handleMutationError(error, {
            fallbackMessage: 'Failed to load assignees.',
            setInlineError: setAssignableUsersError,
          })
        ) {
          return;
        }
      } finally {
        if (active) {
          setIsLoadingAssignableUsers(false);
        }
      }
    };

    loadAssignableUsers();

    return () => {
      active = false;
    };
  }, [activityFormMode, assigneeSearch, handleMutationError]);

  const handleSaveProfile = useCallback(
    async (event) => {
      event.preventDefault();

      if (!connected || notFound || isSavingProfile) {
        return;
      }

      setIsSavingProfile(true);
      setProfileSaveError('');
      setProfileSuccessMessage('');

      try {
        const updatedDetail = await updateCrmCustomerProfile(
          customerNumber,
          buildProfilePayload(profileFormValues)
        );
        setCustomer(updatedDetail?.customer ?? null);
        setProfileFormValues(mapProfileToForm(updatedDetail?.profile));
        setProfileSuccessMessage('CRM profile saved.');
      } catch (error) {
        console.error('Failed to save CRM profile:', error);

        if (
          handleMutationError(error, {
            allowAny409: true,
            allowNotFound: true,
            fallbackMessage: 'Failed to save the CRM profile.',
            setInlineError: setProfileSaveError,
            writeBlockedMessage: 'Fortnox connection required to update CRM profile',
          })
        ) {
          return;
        }
      } finally {
        setIsSavingProfile(false);
      }
    },
    [
      connected,
      customerNumber,
      handleMutationError,
      isSavingProfile,
      notFound,
      profileFormValues,
    ]
  );

  const handleArchivedToggle = useCallback(async () => {
    if (showArchivedContacts) {
      setShowArchivedContacts(false);
      return;
    }

    if (hasLoadedArchivedContacts) {
      setShowArchivedContacts(true);
      return;
    }

    setIsRefreshingContacts(true);
    setContactError('');

    try {
      const loadedContacts = await fetchCrmCustomerContacts(customerNumber, {
        includeArchived: true,
      });
      setContacts(sortContacts(loadedContacts));
      setHasLoadedArchivedContacts(true);
      setShowArchivedContacts(true);
    } catch (error) {
      console.error('Failed to load archived contacts:', error);

      if (
        handleMutationError(error, {
          allowNotFound: true,
          allowViewBlocked: true,
          fallbackMessage: 'Failed to load archived contacts.',
          setInlineError: setContactError,
        })
      ) {
        return;
      }
    } finally {
      setIsRefreshingContacts(false);
    }
  }, [
    customerNumber,
    handleMutationError,
    hasLoadedArchivedContacts,
    showArchivedContacts,
  ]);

  const handleSubmitContact = useCallback(
    async (event) => {
      event.preventDefault();

      if (!contactFormMode || isSavingContact) {
        return;
      }

      const normalizedName = String(contactFormValues.name ?? '').trim();
      if (!normalizedName) {
        setContactError('Contact name is required.');
        return;
      }

      setIsSavingContact(true);
      setContactError('');

      try {
        const payload = buildContactPayload(contactFormValues, {
          active:
            contactFormMode === 'edit'
              ? contacts.find((contact) => Number(contact?.id ?? 0) === Number(editingContactId ?? 0))
                  ?.active ?? true
              : undefined,
        });

        const savedContact =
          contactFormMode === 'create'
            ? await createCrmCustomerContact(customerNumber, payload)
            : await updateCrmCustomerContact(customerNumber, editingContactId, payload);

        setContacts((currentContacts) => mergeContactIntoList(currentContacts, savedContact));
        if (!savedContact?.active && !showArchivedContacts) {
          setHasLoadedArchivedContacts((currentValue) => currentValue || true);
        }
        resetContactForm();
      } catch (error) {
        console.error('Failed to save CRM contact:', error);

        if (
          handleMutationError(error, {
            allowAny409: true,
            fallbackMessage: 'Failed to save the contact.',
            setInlineError: setContactError,
            writeBlockedMessage: 'Fortnox connection required to update CRM data',
          })
        ) {
          return;
        }
      } finally {
        setIsSavingContact(false);
      }
    },
    [
      contactFormMode,
      contactFormValues,
      contacts,
      customerNumber,
      editingContactId,
      handleMutationError,
      isSavingContact,
      resetContactForm,
      showArchivedContacts,
    ]
  );

  const handleToggleContactActive = useCallback(
    async (contact, active) => {
      setIsSavingContact(true);
      setContactError('');

      try {
        const savedContact = await updateCrmCustomerContact(customerNumber, contact?.id, {
          name: String(contact?.name ?? '').trim(),
          title: normalizeOptionalString(contact?.title),
          phone: normalizeOptionalString(contact?.phone),
          email: normalizeOptionalString(contact?.email),
          isPrimary: active ? Boolean(contact?.isPrimary) : false,
          notes: normalizeOptionalString(contact?.notes),
          active,
        });

        setContacts((currentContacts) => mergeContactIntoList(currentContacts, savedContact));
        setHasLoadedArchivedContacts((currentValue) => currentValue || !active);

        if (Number(editingContactId ?? 0) === Number(contact?.id ?? 0)) {
          resetContactForm();
        }
      } catch (error) {
        console.error('Failed to update contact lifecycle:', error);

        if (
          handleMutationError(error, {
            allowAny409: true,
            fallbackMessage: 'Failed to update the contact.',
            setInlineError: setContactError,
            writeBlockedMessage: 'Fortnox connection required to update CRM data',
          })
        ) {
          return;
        }
      } finally {
        setIsSavingContact(false);
      }
    },
    [
      customerNumber,
      editingContactId,
      handleMutationError,
      resetContactForm,
    ]
  );

  const handleSubmitActivity = useCallback(
    async (event) => {
      event.preventDefault();

      if (!activityFormMode || isSavingActivity) {
        return;
      }

      const normalizedTitle = String(activityFormValues.title ?? '').trim();
      if (!normalizedTitle) {
        setActivityError('Activity title is required.');
        return;
      }

      const normalizedState = String(activityFormValues.state ?? 'OPEN').trim() || 'OPEN';
      const normalizedDueDate = toDateInputValue(activityFormValues.dueDate);
      if (normalizedState === 'OPEN' && !normalizedDueDate) {
        setActivityError('Due date is required.');
        return;
      }

      setIsSavingActivity(true);
      setActivityError('');

      try {
        const savedActivity =
          activityFormMode === 'create'
            ? await createCrmCustomerActivity(
                customerNumber,
                buildActivityPayload({ ...activityFormValues, dueDate: normalizedDueDate })
              )
            : await updateCrmCustomerActivity(
                customerNumber,
                editingActivityId,
                buildActivityPayload({ ...activityFormValues, dueDate: normalizedDueDate }, 'OPEN')
              );

        if (isActivityOpen(savedActivity)) {
          setActivities((currentActivities) => mergeActivityIntoList(currentActivities, savedActivity));
        } else {
          setActivities((currentActivities) =>
            currentActivities.filter(
              (currentActivity) => Number(currentActivity?.id ?? 0) !== Number(savedActivity?.id ?? 0)
            )
          );
        }

        if (String(savedActivity?.state ?? '') === 'DONE') {
          setDoneActivities((currentActivities) => mergeDoneActivityIntoList(currentActivities, savedActivity));
        }

        resetActivityForm();
      } catch (error) {
        console.error('Failed to save CRM activity:', error);

        if (
          handleMutationError(error, {
            allowClosedActivity: true,
            fallbackMessage: 'Failed to save the activity.',
            setInlineError: setActivityError,
            writeBlockedMessage: 'Fortnox connection required to update CRM activities',
          })
        ) {
          return;
        }
      } finally {
        setIsSavingActivity(false);
      }
    },
    [
      activityFormMode,
      activityFormValues,
      customerNumber,
      editingActivityId,
      handleMutationError,
      isSavingActivity,
      resetActivityForm,
    ]
  );

  const handleActivityStatusAction = useCallback(
    async (activity, state) => {
      if (isSavingActivity || !isActivityOpen(activity)) {
        return;
      }

      const payload = buildActivityActionPayload(activity, state);
      if (!payload.title || !payload.dueDate) {
        setActivityError('Activity title and due date are required before changing state.');
        return;
      }

      setIsSavingActivity(true);
      setActivityError('');

      try {
        const savedActivity = await updateCrmCustomerActivity(customerNumber, activity?.id, payload);
        setActivities((currentActivities) =>
          currentActivities.filter(
            (currentActivity) => Number(currentActivity?.id ?? 0) !== Number(activity?.id ?? 0)
          )
        );

        if (String(savedActivity?.state ?? '') === 'DONE') {
          setDoneActivities((currentActivities) => mergeDoneActivityIntoList(currentActivities, savedActivity));
        }

        if (Number(editingActivityId ?? 0) === Number(activity?.id ?? 0)) {
          resetActivityForm();
        }
      } catch (error) {
        console.error('Failed to update CRM activity state:', error);

        if (
          handleMutationError(error, {
            allowClosedActivity: true,
            fallbackMessage: 'Failed to update the activity.',
            setInlineError: setActivityError,
            writeBlockedMessage: 'Fortnox connection required to update CRM activities',
          })
        ) {
          return;
        }
      } finally {
        setIsSavingActivity(false);
      }
    },
    [
      customerNumber,
      editingActivityId,
      handleMutationError,
      isSavingActivity,
      resetActivityForm,
    ]
  );

  return {
    customer,
    pageTitle,
    isLoading,
    loadError,
    notFound,
    overview,
    timeline: {
      items: timelineFeedItems,
    },
    contacts: {
      items: visibleContacts,
      activeCount: activeContactCount,
      archivedCount: archivedContactCount,
      showArchivedContacts,
      isRefreshingContacts,
      error: contactError,
      isSaving: isSavingContact,
      formMode: contactFormMode,
      formValues: contactFormValues,
      onFieldChange: handleContactFieldChange,
      onToggleArchived: handleArchivedToggle,
      onStartCreate: handleStartCreateContact,
      onStartEdit: handleStartEditContact,
      onToggleActive: handleToggleContactActive,
      onSubmit: handleSubmitContact,
      onCancel: resetContactForm,
    },
    activities: {
      items: activities,
      error: activityError,
      isSaving: isSavingActivity,
      formMode: activityFormMode,
      formValues: activityFormValues,
      contactOptions: activityContactOptions,
      assignableUsersError,
      isLoadingAssignableUsers,
      assigneeOptions,
      selectedAssignee,
      onFieldChange: handleActivityFieldChange,
      onAssigneeInputChange: handleAssigneeInputChange,
      onAssigneeChange: handleAssigneeChange,
      onStartCreate: handleStartCreateActivity,
      onStartEdit: handleStartEditActivity,
      onStatusAction: handleActivityStatusAction,
      onSubmit: handleSubmitActivity,
      onCancel: resetActivityForm,
    },
    profile: {
      formValues: profileFormValues,
      error: profileSaveError,
      successMessage: profileSuccessMessage,
      isSaving: isSavingProfile,
      onFieldChange: handleProfileFieldChange,
      onSubmit: handleSaveProfile,
    },
  };
}
